import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { sectionId } from '../utils/routes';

const ServicesSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.substring(0, 2) || 'it';
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  
  // Consolidated 5 service categories
  const serviceCategories = [
    'web_branding',
    'ai_video',
    'ai_solutions',
    'mobile_apps',
    'visibility',
    'support'
  ];

  const toggleCategory = (key) => {
    setExpandedCategory(expandedCategory === key ? null : key);
  };

  const getServiceIcon = (key) => {
    const icons = {
      web_branding: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      ai_solutions: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      ai_video: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      mobile_apps: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      visibility: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      support: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    };
    return icons[key] || icons.web_branding;
  };

  return (
    <section id={sectionId(lang, 'services')} className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD700]/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-6 block">
              {t('services.subtitle')}
            </span>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white leading-[0.9]">
              {t('services.title')}
              <br />
              <span className="text-outline">{t('services.title_outline')}</span>
            </h2>
          </motion.div>
          
          <motion.div
            className="flex items-end"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-white/90 text-base md:text-lg font-light leading-relaxed max-w-md">
              {t('services.description')}
            </p>
          </motion.div>
        </div>

        {/* Collapsible Services Grid - 4 Categories */}
        <div className="space-y-0">
          {serviceCategories.map((categoryKey, index) => {
            const isExpanded = expandedCategory === categoryKey;
            const isHovered = hoveredCategory === categoryKey;
            
            return (
              <motion.div
                key={categoryKey}
                className="border-t border-white/10 glass-card glow-hover"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                style={{
                  background: isExpanded ? 'rgba(255, 215, 0, 0.02)' : 'transparent',
                }}
              >
                {/* Category Header - Always Visible & Clickable */}
                <button
                  onClick={() => toggleCategory(categoryKey)}
                  onMouseEnter={() => setHoveredCategory(categoryKey)}
                  onMouseLeave={() => setHoveredCategory(null)}
                  className="w-full py-6 md:py-8 group cursor-pointer text-left"
                  data-testid={`service-category-${categoryKey}`}
                >
                  <div className="grid grid-cols-[auto_1fr_auto] lg:grid-cols-[80px_auto_1fr_auto] items-center gap-4 lg:gap-6">
                    {/* Column 1: Number + Icon */}
                    <div className="flex items-center gap-3 lg:gap-4">
                      <span className={`font-display text-3xl md:text-4xl lg:text-5xl transition-colors duration-500 w-12 lg:w-16 ${isExpanded ? 'text-[#FFD700]' : 'text-[#FFD700]/30 group-hover:text-[#FFD700]/60'}`}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className={`transition-colors duration-500 ${isExpanded ? 'text-[#FFD700]' : 'text-[#FFD700]/40 group-hover:text-[#FFD700]/70'}`}>
                        {getServiceIcon(categoryKey)}
                      </div>
                    </div>
                    
                    {/* Column 2: Title + Subtitle (fixed width on desktop) */}
                    <div className="lg:w-80 xl:w-96">
                      <h3 className={`font-display text-xl md:text-2xl lg:text-3xl transition-colors duration-500 ${isExpanded ? 'text-[#FFD700]' : 'text-white group-hover:text-[#FFD700]'}`}>
                        {t(`services.categories.${categoryKey}.title`)}
                      </h3>
                      <p className="text-white/50 text-xs md:text-sm tracking-wider uppercase mt-0.5">
                        {t(`services.categories.${categoryKey}.subtitle`)}
                      </p>
                    </div>
                    
                    {/* Column 3: Summary (hidden on mobile, aligned on desktop) */}
                    <p className="hidden lg:block text-white/90 text-sm font-light leading-relaxed pr-4">
                      {t(`services.categories.${categoryKey}.summary`)}
                    </p>
                    
                    {/* Column 4: "Scopri" + Arrow */}
                    <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
                      {/* "Scopri" - always visible on mobile, hover on desktop */}
                      <span className={`text-[#FFD700] text-xs font-light tracking-wider whitespace-nowrap lg:hidden ${isExpanded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}>
                        {t('services.discover')}
                      </span>
                      <AnimatePresence>
                        {isHovered && !isExpanded && (
                          <motion.span
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            transition={{ duration: 0.2 }}
                            className="text-[#FFD700] text-sm font-light tracking-wider whitespace-nowrap hidden lg:block"
                          >
                            {t('services.discover')} →
                          </motion.span>
                        )}
                      </AnimatePresence>
                      
                      {/* Arrow button */}
                      <motion.div
                        className={`w-10 h-10 md:w-12 md:h-12 border flex items-center justify-center transition-all duration-500 ${isExpanded ? 'border-[#FFD700] bg-[#FFD700]' : 'border-white/20 group-hover:border-[#FFD700]/50'}`}
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <svg 
                          className={`w-4 h-4 md:w-5 md:h-5 transition-colors duration-500 ${isExpanded ? 'text-black' : 'text-white/50 group-hover:text-[#FFD700]'}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </motion.div>
                    </div>
                  </div>
                </button>
                
                {/* Expandable Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="pb-8 md:pb-10 pl-4 md:pl-20 lg:pl-28">
                        {/* Summary - visible only on mobile inside expansion */}
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="lg:hidden text-[#FFD700]/90 text-sm font-light italic mb-5 pr-4"
                        >
                          "{t(`services.categories.${categoryKey}.summary`)}"
                        </motion.p>
                        
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                          {t(`services.categories.${categoryKey}.items`, { returnObjects: true }).map((item, itemIndex) => (
                            <motion.li 
                              key={item}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3, delay: itemIndex * 0.04 }}
                              className="flex items-center gap-3 text-white/90 text-sm md:text-base font-light"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-[#FFD700]/60 flex-shrink-0" />
                              <span className="leading-normal">{item}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom line */}
        <div className="border-t border-white/10" />

        {/* On Request Note */}
        <motion.p
          className="text-white/60 text-sm text-center mt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {t('services.on_request_note')}
        </motion.p>

        {/* Switzerland Box with animated dashes */}
        <motion.div
          className="mt-8 md:mt-12 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative flex flex-col items-center text-center gap-4 p-6 md:p-8 border border-white/10 bg-white/[0.02] overflow-hidden">
            
            {/* Individual dashes - Center line going up */}
            <div className="absolute left-1/2 -translate-x-1/2 bottom-0 flex flex-col-reverse items-center gap-2">
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={`center-${i}`}
                  className="w-0.5 h-3 bg-[#FFD700]/50"
                  initial={{ opacity: 0, scaleY: 0 }}
                  whileInView={{ opacity: 1, scaleY: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.15, delay: i * 0.1 }}
                />
              ))}
            </div>
            
            {/* Left line - going up then curving right */}
            <div className="absolute left-[15%] bottom-0 flex flex-col-reverse items-center gap-2">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={`left-v-${i}`}
                  className="w-0.5 h-3 bg-[#FFD700]/40"
                  initial={{ opacity: 0, scaleY: 0 }}
                  whileInView={{ opacity: 1, scaleY: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.15, delay: 0.3 + i * 0.1 }}
                />
              ))}
            </div>
            {/* Left horizontal dashes going to center */}
            <div className="absolute left-[15%] top-[35%] flex items-center gap-2">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={`left-h-${i}`}
                  className="w-3 h-0.5 bg-[#FFD700]/40"
                  initial={{ opacity: 0, scaleX: 0 }}
                  whileInView={{ opacity: 1, scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.15, delay: 0.8 + i * 0.1 }}
                />
              ))}
            </div>
            
            {/* Right line - going up then curving left */}
            <div className="absolute right-[15%] bottom-0 flex flex-col-reverse items-center gap-2">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={`right-v-${i}`}
                  className="w-0.5 h-3 bg-[#FFD700]/40"
                  initial={{ opacity: 0, scaleY: 0 }}
                  whileInView={{ opacity: 1, scaleY: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.15, delay: 0.5 + i * 0.1 }}
                />
              ))}
            </div>
            {/* Right horizontal dashes going to center */}
            <div className="absolute right-[15%] top-[35%] flex flex-row-reverse items-center gap-2">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={`right-h-${i}`}
                  className="w-3 h-0.5 bg-[#FFD700]/40"
                  initial={{ opacity: 0, scaleX: 0 }}
                  whileInView={{ opacity: 1, scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.15, delay: 1.0 + i * 0.1 }}
                />
              ))}
            </div>
            
            {/* Icon box - appears after all lines */}
            <motion.div 
              className="w-12 h-12 border border-[#FFD700]/30 flex items-center justify-center bg-[#0a0a0a] relative z-10"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 1.8 }}
            >
              <svg className="w-6 h-6 text-[#FFD700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 2.0 }}
            >
              <h4 className="font-display text-xl md:text-2xl text-white">
                {t('services.switzerland.title')}
              </h4>
              <p className="text-white/90 text-sm mt-1">
                {t('services.switzerland.description')}
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* Final Quote + CTA - Premium Design */}
        <motion.div
          className="mt-20 md:mt-32 relative"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Decorative lines */}
          <div className="absolute left-1/2 -translate-x-1/2 -top-10 w-px h-10 bg-gradient-to-b from-transparent to-[#FFD700]/40" />
          
          <div className="relative border border-[#FFD700]/20 bg-gradient-to-b from-[#FFD700]/[0.03] to-transparent p-8 md:p-12 lg:p-16">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t border-l border-[#FFD700]/40" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-[#FFD700]/40" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-[#FFD700]/40" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-[#FFD700]/40" />
            
            <div className="max-w-3xl mx-auto text-center">
              {/* Main quote */}
              <div className="mb-10">
                <span className="text-[#FFD700] text-4xl md:text-5xl font-display leading-none">"</span>
                <p className="text-white text-xl md:text-2xl lg:text-3xl font-light leading-relaxed -mt-4">
                  {t('services.final_quote')}
                </p>
              </div>
              
              {/* Divider */}
              <div className="flex items-center justify-center gap-4 mb-10">
                <div className="w-12 h-px bg-[#FFD700]/30" />
                <div className="w-2 h-2 rotate-45 border border-[#FFD700]/50" />
                <div className="w-12 h-px bg-[#FFD700]/30" />
              </div>
              
              {/* CTA text */}
              <p className="text-white/90 text-base md:text-lg mb-8 font-light">
                {t('services.cta_text')}
              </p>
              
              {/* CTA Button */}
              <motion.a
                href={`#${sectionId(lang, 'contact')}`}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(sectionId(lang, 'contact'));
                  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-3 group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="services-cta-button"
              >
                <span className="px-8 md:px-10 py-4 md:py-5 bg-[#FFD700] text-black font-display text-sm md:text-base tracking-widest hover:bg-[#FFD700]/90 transition-colors duration-300">
                  {t('services.cta_button')}
                </span>
                <span className="w-12 h-12 md:w-14 md:h-14 border border-[#FFD700] flex items-center justify-center group-hover:bg-[#FFD700] transition-colors duration-300">
                  <svg className="w-5 h-5 text-[#FFD700] group-hover:text-black transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
